import { FC, useEffect, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { ServiceNoReplyEmail } from '../../services/service-noreply-email';
import { TabPage } from '../../components/shared/PageWithTabs/TabPage';
import { TabsType } from '../../components/shared/PageWithTabs/PageWithTabs';
import Card from '../../components/shared/Card/card';
import BtButton from '../bt-button/bt-button';

type Props = { tabs: TabsType };

type Status = {
  isRunningOnThisServer?: boolean;
  currentLock?: { lockId: string; age: number };
  lastSyncTime?: string;
  emailCount?: number;
};

const StatusRow: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
    <span style={{ color: 'var(--gray-700)', fontWeight: 500 }}>{label}</span>
    <span style={{ color: 'var(--gray-900)' }}>{children}</span>
  </div>
);

export const NoReplyEmailTab: FC<Props> = ({ tabs }) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const result = await ServiceNoReplyEmail.getStatus();
      setStatus(result);
    } catch (error: any) {
      console.error('Error fetching status:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleProcessNow = async () => {
    setLoading(true);
    try {
      const result = await ServiceNoReplyEmail.processUnreadEmails();
      if (result.success) {
        NotificationManager.success(`Processed ${result.processed} emails, ${result.added} added to conversations`);
      } else {
        NotificationManager.error('Failed to process emails');
      }
      await fetchStatus();
    } catch (error: any) {
      NotificationManager.error(error?.message || 'Failed to process emails');
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabPage tabs={tabs} title="Email Debug">
      <p style={{ color: 'var(--gray-600)', marginBottom: 24 }}>
        Manage the email processing service for conversation replies.
      </p>

      <Card title="Current Status">
        {status ? (
          <div style={{ marginTop: 16 }}>
            <StatusRow label="Running on this server:">
              <span style={{ color: status.isRunningOnThisServer ? '#10b981' : 'var(--gray-500)', fontWeight: status.isRunningOnThisServer ? 600 : 400 }}>
                {status.isRunningOnThisServer ? '✓ Yes' : '✗ No'}
              </span>
            </StatusRow>
            {status.currentLock && (
              <>
                <StatusRow label="Lock ID:">
                  <code style={{ fontSize: 12, background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{status.currentLock.lockId}</code>
                </StatusRow>
                <StatusRow label="Lock age:">{Math.floor(status.currentLock.age / 1000)}s</StatusRow>
              </>
            )}
            <StatusRow label="Last sync:">
              {status.lastSyncTime ? new Date(status.lastSyncTime).toLocaleString() : 'Never'}
            </StatusRow>
            <StatusRow label="Cached emails:">{status.emailCount}</StatusRow>
          </div>
        ) : (
          <p style={{ marginTop: 16 }}>Loading status...</p>
        )}
      </Card>

      <Card title="Actions">
        <div style={{ marginTop: 16 }}>
          <BtButton onClick={handleProcessNow} disabled={loading}>
            Process Emails Now
          </BtButton>
          <p style={{ marginTop: 16, fontSize: 13, color: 'var(--gray-700)' }}>
            <strong>Process Emails Now:</strong> Manually triggers email processing once. Auto-refresh runs on all servers at startup; one server processes per cycle.
          </p>
        </div>
      </Card>
    </TabPage>
  );
};
